import { useState, useEffect } from "react";
import api from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Slider } from "@/components/ui/slider";
import { 
  Save, 
  RefreshCw,
  User,
  Target,
  DollarSign,
  MapPin,
  Shield,
  X,
  Plus,
  Settings as SettingsIcon,
  Sliders,
  Clock,
  Calendar
} from "lucide-react";
import { toast } from "sonner";

export default function Settings() {
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [newSkill, setNewSkill] = useState("");
  const [newLocation, setNewLocation] = useState("");
  const [newClearance, setNewClearance] = useState("");

  const fetchProfile = async () => {
    try {
      setLoading(true);
      const response = await api.get('/profile');
      setProfile(response.data);
    } catch (error) {
      toast.error("Failed to load profile");
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProfile();
  }, []);

  const handleSave = async () => {
    try {
      setSaving(true);
      await api.put('/profile', profile);
      toast.success("Profile saved successfully");
    } catch (error) {
      toast.error("Failed to save profile");
      console.error(error);
    } finally {
      setSaving(false);
    }
  };

  const addSkill = () => {
    if (newSkill && !profile.skills.includes(newSkill)) {
      setProfile({ ...profile, skills: [...profile.skills, newSkill] });
      setNewSkill("");
    }
  };

  const removeSkill = (skill) => {
    setProfile({ ...profile, skills: profile.skills.filter(s => s !== skill) });
  };

  const addLocation = () => {
    if (newLocation && !profile.preferred_locations.includes(newLocation)) {
      setProfile({ ...profile, preferred_locations: [...profile.preferred_locations, newLocation] });
      setNewLocation("");
    }
  };

  const removeLocation = (location) => {
    setProfile({ ...profile, preferred_locations: profile.preferred_locations.filter(l => l !== location) });
  };

  const addClearance = () => {
    if (newClearance && !profile.preferred_clearance.includes(newClearance)) {
      setProfile({ ...profile, preferred_clearance: [...profile.preferred_clearance, newClearance] });
      setNewClearance("");
    }
  };

  const removeClearance = (clearance) => {
    setProfile({ ...profile, preferred_clearance: profile.preferred_clearance.filter(c => c !== clearance) });
  };

  const updateWeight = (key, value) => {
    setProfile({
      ...profile,
      scoring_weights: { ...profile.scoring_weights, [key]: value[0] }
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="settings-loading">
        <div className="text-center">
          <RefreshCw className="w-12 h-12 animate-spin text-theme-primary mx-auto mb-4" />
          <p className="text-theme-secondary font-mono">LOADING SETTINGS...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-4xl" data-testid="settings-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <div className="w-14 h-12 rounded-xl bg-gradient-to-br from-[#e9c46a] to-[#d4a84b] flex items-center justify-center" style={{boxShadow: '4px 4px 0 rgba(139, 69, 19, 0.4)'}}>
            <Sliders className="w-7 h-7 text-theme-dark" />
          </div>
          <div>
            <h1 className="text-3xl font-bold text-theme-light" style={{textShadow: '2px 2px 4px rgba(0,0,0,0.3)'}}>
              Settings
            </h1>
            <p className="text-theme-muted text-sm font-mono">
              CONFIGURE YOUR PROFILE & PREFERENCES
            </p>
          </div>
        </div>
        <Button 
          onClick={handleSave}
          className="bg-gradient-to-r from-[#e9c46a] to-[#d4a84b] hover:from-[#f4d888] hover:to-[#e9c46a] text-theme-dark border-2 border-[#f4d888]/50 font-bold"
          style={{boxShadow: '3px 3px 0 rgba(139, 69, 19, 0.3)'}}
          disabled={saving}
          data-testid="save-settings-btn"
        >
          {saving ? (
            <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
          ) : (
            <Save className="w-4 h-4 mr-2" />
          )}
          SAVE CHANGES
        </Button>
      </div>

      {/* Profile Info */}
      <div className="rounded-xl overflow-hidden" style={{
        background: 'linear-gradient(145deg, rgba(26, 58, 66, 0.9), rgba(38, 70, 83, 0.8))',
        border: '3px solid rgba(42, 157, 143, 0.4)',
        boxShadow: '6px 6px 0 rgba(38, 70, 83, 0.3)'
      }}>
        <div className="p-5 border-b-2 border-theme-primary/30 bg-theme-darker/50">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-[#2a9d8f] to-[#1a6b62] flex items-center justify-center" style={{boxShadow: '2px 2px 0 rgba(26, 107, 98, 0.4)'}}>
              <User className="w-5 h-5 text-theme-secondary" />
            </div>
            <div>
              <h2 className="text-theme-light font-bold">Profile</h2>
              <p className="text-xs text-theme-muted font-mono">YOUR CONTRACTOR INFO</p>
            </div>
          </div>
        </div>
        <div className="p-5 space-y-5">
          <div>
            <Label className="text-theme-muted font-mono text-xs">NAME</Label>
            <Input
              value={profile?.name || ""}
              onChange={(e) => setProfile({ ...profile, name: e.target.value })}
              className="bg-theme-dark border-2 border-theme-primary/40 mt-1 text-theme-light focus:border-theme-secondary"
              data-testid="profile-name-input"
            />
          </div>
          
          {/* Skills */}
          <div>
            <Label className="text-theme-muted font-mono text-xs">SKILLS</Label>
            <div className="flex flex-wrap gap-2 mt-2">
              {profile?.skills?.map((skill) => (
                <Badge 
                  key={skill}
                  className="bg-theme-primary/20 text-theme-primary-light border-2 border-theme-primary/40 pr-1 font-mono"
                >
                  {skill}
                  <button 
                    onClick={() => removeSkill(skill)}
                    className="ml-1 hover:text-theme-danger transition-colors"
                    data-testid={`remove-skill-${skill}`}
                  >
                    <X className="w-3 h-3" />
                  </button>
                </Badge>
              ))}
            </div>
            <div className="flex gap-2 mt-2">
              <Input
                value={newSkill}
                onChange={(e) => setNewSkill(e.target.value)}
                placeholder="Add skill..."
                className="bg-theme-dark border-2 border-theme-primary/40 text-theme-light"
                onKeyDown={(e) => e.key === "Enter" && addSkill()}
                data-testid="add-skill-input"
              />
              <Button 
                variant="outline" 
                className="border-2 border-theme-primary/40 text-theme-primary-light hover:bg-theme-primary/20"
                onClick={addSkill}
                data-testid="add-skill-btn"
              >
                <Plus className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Rate Preferences */}
      <div className="rounded-xl overflow-hidden" style={{
        background: 'linear-gradient(145deg, rgba(26, 58, 66, 0.9), rgba(38, 70, 83, 0.8))',
        border: '3px solid rgba(233, 196, 106, 0.4)',
        boxShadow: '6px 6px 0 rgba(139, 69, 19, 0.2)'
      }}>
        <div className="p-5 border-b-2 border-theme-secondary/30 bg-theme-darker/50">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-[#e9c46a] to-[#d4a84b] flex items-center justify-center" style={{boxShadow: '2px 2px 0 rgba(139, 69, 19, 0.4)'}}>
                <DollarSign className="w-5 h-5 text-theme-dark" />
              </div>
              <div>
                <h2 className="text-theme-light font-bold">Rate Preferences</h2>
                <p className="text-xs text-theme-muted font-mono">
                  TARGET {profile?.rate_type === "yearly" ? "YEARLY SALARY" : "HOURLY RATE"} RANGE
                </p>
              </div>
            </div>
            
            {/* Rate Type Toggle */}
            <div className="flex items-center gap-1 p-1 rounded-lg bg-theme-dark border border-theme-primary/40">
              <button
                onClick={() => setProfile({ ...profile, rate_type: "hourly" })}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-mono transition-all ${
                  profile?.rate_type === "hourly" 
                    ? "bg-theme-secondary text-theme-dark font-bold" 
                    : "text-theme-muted hover:text-theme-light"
                }`}
                data-testid="rate-type-hourly-btn"
              >
                <Clock className="w-3 h-3" />
                HOURLY
              </button>
              <button
                onClick={() => setProfile({ ...profile, rate_type: "yearly" })}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-mono transition-all ${
                  profile?.rate_type === "yearly" 
                    ? "bg-theme-secondary text-theme-dark font-bold" 
                    : "text-theme-muted hover:text-theme-light"
                }`}
                data-testid="rate-type-yearly-btn"
              >
                <Calendar className="w-3 h-3" />
                YEARLY
              </button>
            </div>
          </div>
        </div>
        <div className="p-5">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="text-theme-muted font-mono text-xs">
                MINIMUM RATE {profile?.rate_type === "yearly" ? "($/YR)" : "($/HR)"}
              </Label>
              <Input
                type="number"
                value={profile?.target_rate_min || 0}
                onChange={(e) => setProfile({ ...profile, target_rate_min: parseInt(e.target.value) || 0 })}
                className="bg-theme-dark border-2 border-theme-primary/40 mt-1 text-theme-secondary font-mono focus:border-theme-secondary"
                placeholder={profile?.rate_type === "yearly" ? "e.g. 150000" : "e.g. 75"}
                data-testid="min-rate-input"
              />
            </div>
            <div>
              <Label className="text-theme-muted font-mono text-xs">
                MAXIMUM RATE {profile?.rate_type === "yearly" ? "($/YR)" : "($/HR)"}
              </Label>
              <Input
                type="number"
                value={profile?.target_rate_max || 0}
                onChange={(e) => setProfile({ ...profile, target_rate_max: parseInt(e.target.value) || 0 })}
                className="bg-theme-dark border-2 border-theme-primary/40 mt-1 text-theme-secondary font-mono focus:border-theme-secondary"
                placeholder={profile?.rate_type === "yearly" ? "e.g. 250000" : "e.g. 125"}
                data-testid="max-rate-input"
              />
            </div>
          </div>
          
          {/* Helper text showing rate conversion */}
          {profile?.target_rate_min > 0 && profile?.target_rate_max > 0 && (
            <div className="mt-4 p-3 rounded-lg bg-theme-dark/50 border border-theme-secondary/20">
              <p className="text-xs text-theme-muted font-mono">
                {profile?.rate_type === "yearly" ? (
                  <>
                    ≈ ${Math.round(profile.target_rate_min / 2080)}/hr - ${Math.round(profile.target_rate_max / 2080)}/hr
                    <span className="text-theme-muted/60 ml-2">(based on 2080 hrs/year)</span>
                  </>
                ) : (
                  <>
                    ≈ ${(profile.target_rate_min * 2080).toLocaleString()}/yr - ${(profile.target_rate_max * 2080).toLocaleString()}/yr
                    <span className="text-theme-muted/60 ml-2">(based on 2080 hrs/year)</span>
                  </>
                )}
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Location Preferences */}
      <div className="rounded-xl overflow-hidden" style={{
        background: 'linear-gradient(145deg, rgba(26, 58, 66, 0.9), rgba(38, 70, 83, 0.8))',
        border: '3px solid rgba(244, 162, 97, 0.4)',
        boxShadow: '6px 6px 0 rgba(139, 69, 19, 0.2)'
      }}>
        <div className="p-5 border-b-2 border-theme-accent/30 bg-theme-darker/50">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-[#f4a261] to-[#e76f51] flex items-center justify-center" style={{boxShadow: '2px 2px 0 rgba(139, 69, 19, 0.4)'}}>
              <MapPin className="w-5 h-5 text-white" />
            </div>
            <div>
              <h2 className="text-theme-light font-bold">Location Preferences</h2>
              <p className="text-xs text-theme-muted font-mono">PREFERRED WORK LOCATIONS</p>
            </div>
          </div>
        </div>
        <div className="p-5">
          <div className="flex flex-wrap gap-2">
            {profile?.preferred_locations?.map((location) => (
              <Badge 
                key={location}
                className="bg-theme-accent/20 text-theme-accent border-2 border-theme-accent/40 pr-1 font-mono"
              >
                {location}
                <button 
                  onClick={() => removeLocation(location)}
                  className="ml-1 hover:text-theme-danger transition-colors"
                  data-testid={`remove-location-${location}`}
                >
                  <X className="w-3 h-3" />
                </button>
              </Badge>
            ))}
          </div>
          <div className="flex gap-2 mt-2">
            <Input
              value={newLocation}
              onChange={(e) => setNewLocation(e.target.value)}
              placeholder="Add location..."
              className="bg-theme-dark border-2 border-theme-primary/40 text-theme-light"
              onKeyDown={(e) => e.key === "Enter" && addLocation()}
              data-testid="add-location-input"
            />
            <Button 
              variant="outline" 
              className="border-2 border-theme-accent/40 text-theme-accent hover:bg-theme-accent/20"
              onClick={addLocation}
              data-testid="add-location-btn"
            >
              <Plus className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </div>

      {/* Clearance Preferences */}
      <div className="rounded-xl overflow-hidden" style={{
        background: 'linear-gradient(145deg, rgba(26, 58, 66, 0.9), rgba(38, 70, 83, 0.8))',
        border: '3px solid rgba(231, 111, 81, 0.4)',
        boxShadow: '6px 6px 0 rgba(139, 69, 19, 0.2)'
      }}>
        <div className="p-5 border-b-2 border-theme-danger/30 bg-theme-darker/50">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-[#e76f51] to-[#d64d32] flex items-center justify-center" style={{boxShadow: '2px 2px 0 rgba(139, 69, 19, 0.4)'}}>
              <Shield className="w-5 h-5 text-white" />
            </div>
            <div>
              <h2 className="text-theme-light font-bold">Clearance Preferences</h2>
              <p className="text-xs text-theme-muted font-mono">SECURITY CLEARANCES</p>
            </div>
          </div>
        </div>
        <div className="p-5">
          <div className="flex flex-wrap gap-2">
            {profile?.preferred_clearance?.map((clearance) => (
              <Badge 
                key={clearance}
                className="bg-theme-danger/20 text-theme-danger border-2 border-theme-danger/40 pr-1 font-mono"
              >
                {clearance}
                <button 
                  onClick={() => removeClearance(clearance)}
                  className="ml-1 hover:text-theme-light transition-colors"
                  data-testid={`remove-clearance-${clearance}`}
                >
                  <X className="w-3 h-3" />
                </button>
              </Badge>
            ))}
          </div>
          <div className="flex gap-2 mt-2">
            <Input
              value={newClearance}
              onChange={(e) => setNewClearance(e.target.value)}
              placeholder="Add clearance..."
              className="bg-theme-dark border-2 border-theme-primary/40 text-theme-light"
              onKeyDown={(e) => e.key === "Enter" && addClearance()}
              data-testid="add-clearance-input"
            />
            <Button 
              variant="outline" 
              className="border-2 border-theme-danger/40 text-theme-danger hover:bg-theme-danger/20"
              onClick={addClearance}
              data-testid="add-clearance-btn"
            >
              <Plus className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </div>

      {/* Scoring Weights */}
      <div className="rounded-xl overflow-hidden" style={{
        background: 'linear-gradient(145deg, rgba(26, 58, 66, 0.9), rgba(38, 70, 83, 0.8))',
        border: '3px solid rgba(64, 201, 186, 0.4)',
        boxShadow: '6px 6px 0 rgba(38, 70, 83, 0.3)'
      }}>
        <div className="p-5 border-b-2 border-[#40c9ba]/30 bg-theme-darker/50">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-[#40c9ba] to-[#2a9d8f] flex items-center justify-center" style={{boxShadow: '2px 2px 0 rgba(26, 107, 98, 0.4)'}}>
              <Target className="w-5 h-5 text-theme-dark" />
            </div>
            <div>
              <h2 className="text-theme-light font-bold">Scoring Weights</h2>
              <p className="text-xs text-theme-muted font-mono">ADJUST HOW LEADS ARE SCORED (TOTAL = 100)</p>
            </div>
          </div>
        </div>
        <div className="p-5 space-y-5">
          {profile?.scoring_weights && Object.entries(profile.scoring_weights).map(([key, value]) => (
            <div key={key}>
              <div className="flex items-center justify-between mb-2">
                <Label className="text-theme-muted capitalize font-mono text-sm">
                  {key.replace('_', ' ')}
                </Label>
                <span className="font-mono text-theme-secondary font-bold">{value}</span>
              </div>
              <Slider
                value={[value]}
                onValueChange={(val) => updateWeight(key, val)}
                max={100}
                step={5}
                className="w-full"
                data-testid={`weight-${key}-slider`}
              />
            </div>
          ))}
          <div className="p-4 rounded-lg bg-theme-dark/50 border-2 border-theme-primary/30">
            <div className="flex items-center justify-between">
              <span className="text-sm text-theme-muted font-mono">TOTAL WEIGHT</span>
              <span className={`font-mono font-bold text-lg ${
                Object.values(profile?.scoring_weights || {}).reduce((a, b) => a + b, 0) === 100
                  ? "text-emerald-400"
                  : "text-theme-danger"
              }`} style={{textShadow: '0 0 10px currentColor'}}>
                {Object.values(profile?.scoring_weights || {}).reduce((a, b) => a + b, 0)} / 100
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
